/**
 * LocaleSync — Sprint EA-4.
 *
 * Bridges `useLang()` (state + localStorage, declared in
 * `I18nProvider`) with the persisted `users.locale` server-
 * side. Two responsibilities:
 *
 *   1. **Hydrate on login.** When `auth` transitions from
 *      null → authenticated we fetch `GET /me`. If the
 *      server returned a non-null `locale`, we call
 *      `setLang(locale)` so the UI reflects the user's
 *      stored choice (which may differ from the localStorage
 *      value left by a different browser session or the
 *      browser-default heuristic in `detectInitialLang`).
 *
 *   2. **Persist on change.** Once we've hydrated, every
 *      subsequent `lang` change calls
 *      `meApi.setLocale(lang)` fire-and-forget. The server
 *      now drives the welcome / verify / reset email
 *      language for this user.
 *
 * Why not bake this into `I18nProvider` directly:
 *
 *   - I18nProvider doesn't depend on `useAuth()`; coupling
 *     them would force every page (including the Login
 *     screen) into the auth tree. The Login page must
 *     mount before auth — `useT` works without a token.
 *
 *   - LocaleSync is mounted once, inside `<AuthProvider>`,
 *     and has no DOM output. It's a pure side-effect node.
 *
 * Failure handling: every server call is swallowed
 * silently. A failed hydrate just means we keep whatever
 * localStorage / browser-default produced; a failed
 * persist means the next browser session might disagree,
 * but the UI keeps working. Both are cosmetic regressions,
 * not correctness failures.
 */

import { useEffect, useRef } from "react";

import { useAuth } from "../auth/AuthProvider";
import { meApi } from "../api";
import { useLang } from "./I18nProvider";
import type { Lang } from "./messages";


function isLang(v: unknown): v is Lang {
  return v === "ru" || v === "en";
}


export function LocaleSync(): null {
  const { auth }      = useAuth();
  const { lang, setLang } = useLang();

  // Track whether hydration has run for the current auth
  // session. We hydrate exactly once per login so the
  // server-side value doesn't keep clobbering local
  // changes on every re-render.
  const hydratedForUserRef = useRef<string | null>(null);

  // ----- 1. hydrate on login --------------------------------------------
  useEffect(() => {
    if (!auth) {
      hydratedForUserRef.current = null;
      return;
    }
    if (hydratedForUserRef.current === auth.claims.sub) return;
    hydratedForUserRef.current = auth.claims.sub;

    let alive = true;
    meApi.get()
      .then((me) => {
        if (!alive) return;
        if (isLang(me.locale)) setLang(me.locale);
      })
      .catch(() => { /* cosmetic — keep localStorage value */ });

    return () => { alive = false; };
  }, [auth, setLang]);

  // ----- 2. persist on change -------------------------------------------
  useEffect(() => {
    // Only persist after we've hydrated for this user — otherwise
    // the first render after login would PATCH the just-hydrated
    // value back into a no-op (cheap, but noisy in network logs).
    if (!auth) return;
    if (hydratedForUserRef.current !== auth.claims.sub) return;

    meApi.setLocale(lang).catch(() => {
      /* swallow — see file docstring */
    });
  }, [auth, lang]);

  return null;
}
